import type { SupabaseClient } from '../supabase/client';
import type {
  Product,
  PublishProductInput,
  PublishProductResult,
  PublishProductErrorKey,
} from '../types/product';

/**
 * Publish a single product. Calls `publish_product(uuid, true)` which
 * atomically:
 *   1. verifies `auth.uid() IS NOT NULL`                (42501 otherwise)
 *   2. takes FOR UPDATE row lock + resolves store_id    (42501 otherwise — CWE-203)
 *      and verifies is_store_admin()
 *   3. verifies the product exists & not soft-deleted   (P0002 otherwise)
 *   4. verifies parent store is published               (P0004 otherwise)
 *   5. verifies inventory > 0                           (P0005 otherwise)
 *   6. verifies active same-store category               (P0006 otherwise)
 *   7. flips `products.published = true`
 *   6b. GET DIAGNOSTICS ROW_COUNT guard                 (P0002 otherwise —
 *       catches concurrent soft-delete)
 *   8. SELECTs and returns the updated products row
 *
 * Throws on failure; caller maps the error via mapPublishProductError.
 */
export async function publishProduct(
  client: SupabaseClient,
  input: PublishProductInput,
): Promise<PublishProductResult> {
  return invokePublishProductRpc(client, input.productId, true);
}

/**
 * Unpublish a single product. Calls `publish_product(uuid, false)`.
 * The RPC skips gates 4 and 5 server-side so a product can always be
 * unpublished as long as the caller is authorized.
 */
export async function unpublishProduct(
  client: SupabaseClient,
  input: PublishProductInput,
): Promise<PublishProductResult> {
  return invokePublishProductRpc(client, input.productId, false);
}

/**
 * Shared RPC-invocation helper for {@link publishProduct} and
 * {@link unpublishProduct}. Kept private to this module.
 *
 * The RPC returns the updated `products` row (RETURNS public.products).
 * Supabase JS maps this to a single-row object on success — no unwrap
 * needed.
 */
async function invokePublishProductRpc(
  client: SupabaseClient,
  productId: string,
  publish: boolean,
): Promise<Product> {
  const { data, error } = await client.rpc('publish_product', {
    target_product_id: productId,
    publish,
  });
  if (error) throw error;
  if (!data) {
    // Defensive — the RPC RETURNS public.products so `data` is always
    // a row on success. If we ever reach this branch the server
    // contract changed under us; surface a clear error rather than
    // cast null.
    throw new Error('[publishProduct] RPC returned no row');
  }
  return data as Product;
}

/**
 * Code-first error-to-translation-key mapper (AC5). NO regex fallbacks
 * on err.message — every branch keys on SQLSTATE or JS error shape.
 * Mirrors F-004 §5.2 / §12.2 invariant.
 *
 * Branches (8, in order):
 *   1. null / undefined / non-object           → 'unknown'
 *   2. TypeError with /fetch/i message         → 'networkFailed'
 *   3. name === 'AuthRetryableFetchError'      → 'networkFailed'
 *   4. code === '42501'                        → 'permissionDenied'
 *   5. code === 'P0002'                        → 'notFound'
 *   6. code === 'P0004'                        → 'storeNotPublished'
 *   7. code === 'P0005'                        → 'insufficientInventory'
 *   8. code === 'P0006'                        → 'categoryRequired'
 *   9. fall-through                            → 'unknown'
 */
export function mapPublishProductError(err: unknown): PublishProductErrorKey {
  // Branch 1: null / undefined / non-object.
  if (!err || typeof err !== 'object') {
    return 'admin.products.publish.errors.unknown';
  }

  // Branch 2: TypeError('fetch') — thrown by the browser when the
  // network is unreachable mid-request.
  if (err instanceof TypeError && /fetch/i.test((err as Error).message ?? '')) {
    return 'admin.products.publish.errors.networkFailed';
  }

  const maybe = err as {
    code?: string;
    message?: string;
    name?: string;
  };

  // Branch 3: supabase-js auth retry exhaustion.
  if (maybe.name === 'AuthRetryableFetchError') {
    return 'admin.products.publish.errors.networkFailed';
  }

  // Branches 4-7: SQLSTATE codes from the RPC.
  if (maybe.code === '42501') {
    return 'admin.products.publish.errors.permissionDenied';
  }

  if (maybe.code === 'P0002') {
    return 'admin.products.publish.errors.notFound';
  }

  if (maybe.code === 'P0004') {
    return 'admin.products.publish.errors.storeNotPublished';
  }

  if (maybe.code === 'P0005') {
    return 'admin.products.publish.errors.insufficientInventory';
  }

  if (maybe.code === 'P0006') {
    return 'admin.products.publish.errors.categoryRequired';
  }
  if (maybe.code === 'P0606') {
    return 'admin.entitlements.productLimitBlocked';
  }
  if (maybe.code === 'P0601') {
    return 'admin.entitlements.upgradePrompt.description';
  }

  // Branch 9: fall-through.
  return 'admin.products.publish.errors.unknown';
}
